import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Alert
} from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { getGraphQLErrorMessage } from "utils/graphqlError"; // ✅

const CREATE_LOAN_PRODUCT = gql`
  mutation CreateLoanProduct($newLoanProduct: LoanProductsInput!) {
    createLoanProduct(newLoanProduct: $newLoanProduct) {
      id
      name
      interestRate
      durationInMonths
      isActive
      createdDate
      createdBy
      updatedDate
      updatedBy
      groupId
    }
  }
`;

const CreateLoanProductForm = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [durationInMonths, setDurationInMonths] = useState("");
  const [friendlyError, setFriendlyError] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setInterestRate("");
      setDurationInMonths("");
      setFriendlyError("");
    }
  }, [open]);

  const [createLoanProduct, { loading }] = useMutation(CREATE_LOAN_PRODUCT, {
    onCompleted: async () => {
      setFriendlyError("");
      if (onCreated) await onCreated();
    },
    onError: (err) => {
      setFriendlyError(getGraphQLErrorMessage(err)); // ✅ shows "You are not authorized..."
    },
  });

  const canSubmit = useMemo(() => {
    const ir = Number(interestRate);
    const dur = Number(durationInMonths);
    return (
      name.trim().length > 0 &&
      !Number.isNaN(ir) && ir > 0 &&
      !Number.isNaN(dur) && dur > 0
    );
  }, [name, interestRate, durationInMonths]);

  const submit = () => {
    if (!canSubmit) return;

    createLoanProduct({
      variables: {
        newLoanProduct: {
          name: name.trim(),
          interestRate: Number(interestRate),
          durationInMonths: Number(durationInMonths),
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Loan Product</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Product Name"
            margin="dense"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />

          <TextField
            fullWidth
            type="number"
            label="Interest Rate (%)"
            margin="dense"
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            disabled={loading}
          />

          <TextField
            fullWidth
            type="number"
            label="Duration (Months)"
            margin="dense"
            value={durationInMonths}
            onChange={(e) => setDurationInMonths(e.target.value)}
            disabled={loading}
          />

          {friendlyError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {friendlyError}
            </Alert>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading || !canSubmit}>
          {loading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLoanProductForm;